import { reportVerbiage } from '@chiroup/core/constants/reportVerbiage';
import { Languages } from '@chiroup/core/enums/Languages.enum';
import { createPrescription } from '@chiroup/core/functions/createPrescription';
import { formatPhone } from '@chiroup/core/functions/format';
import { getId } from '@chiroup/core/functions/getId';
import { tiptapDocHasText, TiptapJSON } from '@chiroup/core/functions/tiptapDocHasText';
import { Condition } from '@chiroup/core/types/Condition.type';
import { CustomSection } from '@chiroup/core/types/CustomSection.type';
import { ExercisesByPhase } from '@chiroup/core/types/Exercise.type';
import { InOfficeVisit } from '@chiroup/core/types/InOfficeVisit.type';
import { ReportLocationInfo } from '@chiroup/core/types/ReportPrint.type';
import { TriangleAlert as ExclamationTriangleIcon, User as UserIcon } from 'lucide-react';
import React from 'react';
import { AlertBanner } from '../AlertBanner';
import { EditorView } from '../form/EditorView';
import { Icon } from '../Icon';
import { Icons } from '../icons';
import HealthcomQR from '../images/healthcom-qr.png';
import HealthcomLogo from '../images/healthcom.png';
import ReportPrintExercises from './ReportPrintExercises';
import ReportPrintHeader from './ReportPrintHeader';
import ReportPrintLetterhead from './ReportPrintLetterhead';
import ReportPrintPageBreak from './ReportPrintPageBreak';

const S3_URL = import.meta.env.VITE_S3_UPLOAD_URL;

const createPrescriptions = (language: Languages, visits: InOfficeVisit[]) => {
  return visits
    ?.map(visit => createPrescription(language, visit))
    .join(`, ${reportVerbiage[language === 'es' ? 'es' : 'en'].then} `);
};

const createVisitVerbiage = (
  language: Languages,
  visits: InOfficeVisit[],
  goalPercent?: number
) => {
  let visitText = visits?.length
    ? `${reportVerbiage[language === 'es' ? 'es' : 'en'].treatment1} ${createPrescriptions(
        language,
        visits
      )}. `
    : '';

  if (goalPercent) {
    visitText += `${reportVerbiage[language === 'es' ? 'es' : 'en'].treatment2} ${goalPercent}% ${
      reportVerbiage[language === 'es' ? 'es' : 'en'].treatment3
    } `;
  }

  if (visits?.length) {
    visitText += reportVerbiage[language === 'es' ? 'es' : 'en'].treatment4;
  }

  return visitText;
};

type Props = {
  // Email + phone drive the OTP-based login copy on the report. If
  // neither is on file we skip the HealthCom block entirely and
  // surface a clinician-facing alert instead.
  patientEmail?: string | null;
  patientPhone?: string | null;
  language?: Languages;
  clinicName?: string;
  summary?: string | TiptapJSON;
  patientName?: string;
  reportDate?: string;
  examFindings?: string | TiptapJSON;
  conditions?: Condition[];
  treatments?: number[];
  treatmentOptions?: any[];
  visits?: InOfficeVisit[];
  goalPercent?: number;
  additionalGoals?: string | TiptapJSON;
  advice?: number[];
  adviceOptions?: any[];
  customSections?: CustomSection[];
  exercises: ExercisesByPhase;
  exercisePlan?: boolean;
  chartCopy?: boolean;
  providerName?: string;
  diagnosis?: string;
  pcpName?: string;
  printPreview?: boolean;
  pdf?: boolean;
  hideLetterheadOnChartCopy?: boolean;
  blobUrl?: string;
  /** Clinic branding colour applied to headings, callouts and the HealthCom box. */
  primaryColor?: string;
  /** Clinic ID — only used for the Premier Rehab (5) shop banner. */
  clinicId?: number;
  /** Already-resolved letterhead logo URL — see resolveLetterheadLogoUrl. */
  logoUrl?: string;
  locationInfo: ReportLocationInfo;
};

const ReportPrint: React.FC<Props> = ({
  patientEmail,
  patientPhone,
  language = Languages.en,
  clinicName,
  summary,
  patientName,
  reportDate,
  examFindings,
  conditions,
  treatments,
  treatmentOptions,
  visits,
  goalPercent,
  additionalGoals,
  advice,
  adviceOptions,
  customSections,
  exercises,
  exercisePlan,
  chartCopy,
  providerName,
  diagnosis,
  printPreview = false,
  pdf = false,
  hideLetterheadOnChartCopy = true,
  blobUrl,
  primaryColor,
  clinicId,
  logoUrl,
  locationInfo,
}) => {
  const isSpanish = language === Languages.es;
  const languageToUse = isSpanish ? Languages.es : Languages.en;

  // HealthCom now uses email/phone + OTP login. The HealthCom access box
  // and the "patient has no contact" warning are both gated on whether
  // the patient has a reachable contact. Computed at the component scope
  // so the warning can render at the top of the page while the access
  // box stays in its in-flow slot in the exercises section.
  const trimmedEmail = patientEmail?.trim() || '';
  const trimmedPhone = patientPhone?.trim() || '';
  const hasContact = !!(trimmedEmail || trimmedPhone);
  const hasCurrentExercises = !!exercises?.[1]?.length;
  const showNoContactAlert = !hasContact && hasCurrentExercises;

  return (
    <div id={pdf ? 'report-print' : 'report-print-not-pdf'} className={pdf ? 'hidden' : ''}>
      <div
        className={[
          pdf
            ? 'mt-0 text-sm text-gray-800'
            : printPreview
              ? 'mt-0 text-sm text-gray-800'
              : 'hidden print:block text-sm text-gray-800 bg-white',
          'report-print',
        ].join(' ')}
        style={printPreview ? { width: 800, minWidth: 800 } : { letterSpacing: 0.2 }}
      >
        {/* Clinician-facing alert above the paper. On-screen only —
            print:hidden keeps it out of the printed/PDF report so the
            patient's handout doesn't carry a staff-only warning. Only
            relevant when there are current exercises (the HealthCom
            sign-in box is what the contact is for). */}
        {showNoContactAlert && (
          <AlertBanner severity="warning" className="my-4 print:hidden">
            <span className="font-medium">Action needed: </span>
            This patient has no email address or phone number on record, so they will <b>not</b> be
            able to sign in to HealthCom. Add an email or phone to their chart and re-print this
            report to include sign-in instructions.
          </AlertBanner>
        )}
        <div
          className={
            pdf
              ? 'bg-white shadow-md mt-2 overflow-hidden'
              : printPreview
                ? 'bg-white shadow-md mt-2 p-12 overflow-hidden'
                : 'px-6'
          }
        >
          {!!chartCopy && (
            <div
              className="relative text-xs page-break"
              style={printPreview ? { minHeight: 950 } : {}}
            >
              {!hideLetterheadOnChartCopy && (
                <ReportPrintLetterhead
                  logoUrl={logoUrl}
                  clinicName={clinicName}
                  blobUrl={blobUrl}
                  pdf={pdf}
                  locationInfo={locationInfo}
                />
              )}
              <div className="font-semibold">Chart copy</div>
              <div>Patient name: {patientName}</div>
              <div>Prescribed on: {reportDate}</div>
              <div>{providerName || ''}</div>
              <div className="my-6">
                <span>Diagnosis:</span> {diagnosis}
              </div>
              {!!exercises?.[1]?.length && (
                <ReportPrintExercises
                  primaryColor={primaryColor}
                  title={reportVerbiage[languageToUse].currentExercises}
                  exercises={exercises[1]}
                  language={languageToUse}
                  pdf={pdf}
                  small
                />
              )}
              {!!exercises?.[2]?.length && (
                <ReportPrintExercises
                  primaryColor={primaryColor}
                  title={reportVerbiage[languageToUse].upcomingExercises}
                  exercises={exercises[2]}
                  language={languageToUse}
                  pdf={pdf}
                  small
                />
              )}
            </div>
          )}
          {!!summary && (
            <div className="page-break" style={printPreview ? { minHeight: 950 } : {}}>
              {!!chartCopy && <ReportPrintPageBreak printPreview={printPreview} />}
              <div className="relative block">
                <div className="absolute left-0 top-0 w-full">
                  <ReportPrintLetterhead
                    logoUrl={logoUrl}
                    blobUrl={blobUrl}
                    clinicName={clinicName}
                    pdf={pdf}
                    locationInfo={locationInfo}
                  />
                </div>
                <EditorView
                  className="pt-36"
                  value={
                    typeof summary === 'string' && summary.startsWith('{')
                      ? JSON.parse(summary)
                      : summary
                  }
                />
              </div>
            </div>
          )}
          <div style={printPreview ? { minHeight: 950 } : {}}>
            {(!!summary || !!chartCopy) && <ReportPrintPageBreak printPreview={printPreview} />}
            <div className="relative block">
              <ReportPrintLetterhead
                logoUrl={logoUrl}
                blobUrl={blobUrl}
                clinicName={clinicName}
                pdf={pdf}
                locationInfo={locationInfo}
              />
              <div className="inline-block w-full text-center">
                <div className="flex flex-col w-full">
                  <h1 className="flex-1 text-3xl leading-6 font-extrabold">
                    {exercisePlan
                      ? reportVerbiage[languageToUse].yourExercisePlan
                      : reportVerbiage[languageToUse].aboutYourCondition}
                  </h1>
                  <div className="mt-4">
                    <div className="inline-block gap-6 -ml-1">
                      <div className="flex items-center text-sm text-gray-500">
                        {pdf ? (
                          <div className="flex items-center text-sm text-gray-500">
                            <div className="relative h-5 w-5 mt-3">
                              <UserIcon className="h-5 w-5 text-gray-500 absolute left-0 top-0" />
                            </div>
                            <span className="ml-1">{patientName}</span>
                            {!!reportDate && (
                              <>
                                <div className="relative h-5 w-5 mt-3 ml-4">
                                  <Icon
                                    icon={Icons.calendar}
                                    className="h-5 w-5 text-gray-500 absolute left-0 top-0"
                                  />
                                </div>
                                <span className="ml-1">{reportDate}</span>
                              </>
                            )}
                          </div>
                        ) : (
                          <>
                            <UserIcon className="shrink-0 mr-1.5 h-5 w-5 text-gray-500" />
                            {patientName}
                            {!!reportDate && (
                              <>
                                <Icon
                                  icon={Icons.calendar}
                                  className="shrink-0 mr-1.5 h-5 w-5 text-gray-500 ml-6"
                                />
                                {reportDate}
                              </>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {!!examFindings && (examFindings as any)?.content?.[0]?.content?.length && (
              <div className="mt-12 avoid-break">
                <ReportPrintHeader
                  primaryColor={primaryColor}
                  pdf={pdf}
                  title={reportVerbiage[languageToUse].keyExamFindings}
                />
                <EditorView className="relative my-6" value={examFindings} />
              </div>
            )}
            {!exercisePlan && !!conditions?.length && (
              <div className="mt-12">
                <div className="relative">
                  {conditions.map((condition, index) => (
                    <div
                      className={[
                        'inline-block print:block w-full',
                        index === 0 ? '' : 'avoid-break',
                      ].join(' ')}
                      key={index}
                    >
                      {index === 0 && (
                        <ReportPrintHeader
                          primaryColor={primaryColor}
                          pdf={pdf}
                          title={reportVerbiage[languageToUse].whatsWrong}
                        />
                      )}
                      <div className={[index === 0 && !pdf ? '' : 'mt-4'].join(' ')}>
                        {condition.commonImg && (
                          <div style={{ width: 150, height: 150 }} className="mr-4 float-left">
                            <img
                              src={
                                condition.commonImg
                                  ? condition.commonImg.startsWith('http')
                                    ? condition.commonImg
                                    : `${import.meta.env.VITE_S3_ASSETS_URL}/${condition.commonImg}`
                                  : ''
                              }
                              alt={condition.commonName}
                              width="150px"
                              height="auto"
                              style={{ maxHeight: '100%', maxWidth: '100%' }}
                            />
                          </div>
                        )}
                        <div>
                          <p
                            className="text-md font-extrabold"
                            style={{
                              color: primaryColor,
                            }}
                          >
                            {isSpanish && condition.spanishName
                              ? condition.spanishName
                              : condition.commonName}
                          </p>
                          <EditorView
                            value={
                              (isSpanish && condition.spanishDescr
                                ? condition.spanishDescr
                                : condition.descr) || ''
                            }
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {!exercisePlan && !!treatments?.length && (
              <div className="mt-12">
                {treatments?.map((treatment, index) => {
                  const theTreatment = treatmentOptions?.find(t => getId(t) === treatment);

                  return (
                    <div key={index}>
                      {index === 0 && (
                        <ReportPrintHeader
                          primaryColor={primaryColor}
                          pdf={pdf}
                          title={reportVerbiage[languageToUse].thingsWeCanDoToHelpYou}
                        />
                      )}
                      <div
                        className={['relative w-full', index === 0 && !pdf ? '' : 'mt-4'].join(' ')}
                        key={index}
                      >
                        <div className="flex">
                          {theTreatment?.img && (
                            <div style={{ width: 100, height: 100 }} className="mr-4">
                              <img
                                src={
                                  theTreatment?.img
                                    ? theTreatment?.img.startsWith('http')
                                      ? theTreatment?.img
                                      : `${import.meta.env.VITE_S3_ASSETS_URL}/${theTreatment?.img}`
                                    : ''
                                }
                                alt={theTreatment.name}
                                width="100px"
                                height="auto"
                                style={{ width: 100, maxWidth: 100 }}
                                onError={({ currentTarget }) => {
                                  currentTarget.onerror = null;
                                  currentTarget.className = 'hidden';
                                }}
                              />
                            </div>
                          )}
                          <div className="">
                            <p
                              className="text-lg font-extrabold"
                              style={{
                                color: primaryColor,
                              }}
                            >
                              {isSpanish && theTreatment?.spanishName
                                ? theTreatment?.spanishName
                                : theTreatment?.name}
                            </p>
                            <div>
                              <EditorView
                                value={
                                  (isSpanish && theTreatment?.spanishDescr
                                    ? theTreatment?.spanishDescr
                                    : theTreatment?.descr) || ''
                                }
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {(!!visits?.length || !!goalPercent || tiptapDocHasText(additionalGoals)) && (
              <div className="mt-12 avoid-break">
                <ReportPrintHeader
                  primaryColor={primaryColor}
                  pdf={pdf}
                  title={reportVerbiage[languageToUse].treatmentGoalsAndOutcomeMeasures}
                />
                <div className="block">
                  <p>{createVisitVerbiage(languageToUse, visits || [], goalPercent)}</p>
                  <br />
                  {tiptapDocHasText(additionalGoals) && <EditorView value={additionalGoals} />}
                </div>
              </div>
            )}
            {!!advice?.length && (
              <div className="mt-12">
                {advice.map((adv, index) => {
                  const divv = adviceOptions?.find(t => getId(t) === adv);

                  return (
                    <div className="relative w-full" key={index}>
                      {index === 0 && (
                        <ReportPrintHeader
                          primaryColor={primaryColor}
                          pdf={pdf}
                          title={reportVerbiage[languageToUse].thingsYouCanDoToHelpYourself}
                        />
                      )}
                      <div className={['flex', index === 0 && !pdf ? '' : 'mt-4'].join(' ')}>
                        {divv?.icon && (
                          <div style={{ width: 100, height: 100 }} className="mr-4">
                            <img
                              src={divv.icon}
                              alt={divv.name}
                              width="100px"
                              height="auto"
                              style={{ width: 100, maxWidth: 100 }}
                              onError={({ currentTarget }) => {
                                currentTarget.onerror = null;
                                currentTarget.className = 'hidden';
                              }}
                            />
                          </div>
                        )}
                        <div className="block">
                          <p
                            className="text-lg font-extrabold"
                            style={{
                              color: primaryColor,
                            }}
                          >
                            {isSpanish && divv?.spanishName ? divv?.spanishName : divv?.name}
                          </p>
                          <div>
                            <EditorView
                              value={
                                (isSpanish && divv?.spanishDescr
                                  ? divv?.spanishDescr
                                  : divv?.descr) || ''
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {customSections?.map((section, i) => (
              <div className="mt-12 avoid-break" key={i}>
                <ReportPrintHeader primaryColor={primaryColor} pdf={pdf} title={section.title} />
                <EditorView className="relative mb-6" value={section.descr || ''} />
              </div>
            ))}
            {hasCurrentExercises && (
              <div>
                <div className="mt-12 avoid-break">
                  <ReportPrintHeader
                    primaryColor={primaryColor}
                    pdf={pdf}
                    title={reportVerbiage[languageToUse].exercisesAtHome}
                  />
                  <p className="mt-2 text-sm text-gray-800">
                    {reportVerbiage[languageToUse].theFollowingExercises}
                  </p>
                  {hasContact && (
                    <div className="w-full py-6 avoid-break" style={{ minHeight: 350 }}>
                      <div
                        className="flex flex-col max-w-lg border-4 mx-auto rounded-lg"
                        style={{
                          borderColor: primaryColor,
                          height: 262,
                          maxHeight: 262,
                        }}
                      >
                        <div
                          className={[
                            'w-full text-white text-xl font-extrabold mx-auto text-center',
                            pdf ? 'pb-6' : 'py-4',
                          ].join(' ')}
                          style={{
                            backgroundColor: primaryColor,
                          }}
                        >
                          {reportVerbiage[languageToUse].accessYourExerciseVideos}
                        </div>
                        <div className="inline-block relative h-48">
                          <div
                            className="border-b-0 border-r relative h-48 w-1/2 float-left"
                            style={{
                              borderColor: primaryColor,
                            }}
                          >
                            <div className="p-7 block">
                              <div
                                className="text-white text-lg font-semibold mx-auto rounded-lg p-1 text-center mb-4 w-32 h-8 flex justify-center items-center"
                                style={{
                                  backgroundColor: primaryColor,
                                }}
                              >
                                <span style={pdf ? { marginTop: -13 } : {}}>
                                  {reportVerbiage[languageToUse].scanMe}
                                </span>
                              </div>
                              <a href="https://app.healthcom.io" target="_blank" rel="noreferrer">
                                <div style={{ height: 80, width: 80 }} className="mx-auto">
                                  <img
                                    src={HealthcomQR}
                                    width="80px"
                                    height="auto"
                                    alt="Visit app.healthcom.io"
                                  />
                                </div>
                              </a>
                            </div>
                            <div className="block absolute right-0 transform translate-x-4 top-1/2 -translate-y-4">
                              <p
                                className={[
                                  'font-semibold text-white rounded-full flex items-center justify-center text-md h-8 w-8',
                                  pdf ? 'absolute' : '',
                                ].join(' ')}
                                style={
                                  pdf
                                    ? {
                                        backgroundColor: primaryColor,
                                        left: -47,
                                        top: 15,
                                      }
                                    : {
                                        backgroundColor: primaryColor,
                                      }
                                }
                              >
                                <span
                                  className="uppercase"
                                  style={
                                    pdf
                                      ? { marginTop: -14 }
                                      : {
                                          marginTop: '-0.05rem !important',
                                        }
                                  }
                                >
                                  {reportVerbiage[languageToUse].or}
                                </span>
                              </p>
                            </div>
                          </div>
                          <div className="p-7 block w-1/2 float-left">
                            <a
                              href="https://app.healthcom.io"
                              target="_blank"
                              rel="noreferrer"
                              className="no-underline"
                            >
                              <div style={{ height: 82, width: 90 }} className="mx-auto mb-4">
                                <img
                                  src={HealthcomLogo}
                                  alt="HealthCom"
                                  width="90px"
                                  height="auto"
                                  className="mb-4"
                                />
                              </div>
                              <div
                                className="text-white text-lg font-semibold mx-auto rounded-lg p-1 text-center w-52 h-8 flex justify-center items-center"
                                style={{
                                  backgroundColor: primaryColor,
                                }}
                              >
                                <span style={pdf ? { marginTop: -13 } : {}}>
                                  {reportVerbiage[languageToUse].visitHealthCom}
                                </span>
                              </div>
                            </a>
                          </div>
                        </div>
                      </div>
                      <div className="px-4">
                        <p className="text-sm text-gray-700 text-center pt-4">
                          {reportVerbiage[languageToUse].signInInstructions}
                        </p>
                        <div className="text-base text-gray-700 font-semibold text-center pt-2">
                          {trimmedEmail && (
                            <p>
                              {reportVerbiage[languageToUse].yourEmailOnFile}: <b>{trimmedEmail}</b>
                            </p>
                          )}
                          {trimmedPhone && (
                            <p>
                              {reportVerbiage[languageToUse].yourPhoneOnFile}:{' '}
                              <b>{formatPhone(trimmedPhone)}</b>
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="mt-12">
                  <ReportPrintExercises
                    primaryColor={primaryColor}
                    pdf={pdf}
                    title={reportVerbiage[languageToUse].currentExercises}
                    exercises={exercises[1]}
                    language={language}
                  />
                </div>
              </div>
            )}
            {!!exercises?.[2]?.length && (
              <div className="mt-12">
                <ReportPrintExercises
                  primaryColor={primaryColor}
                  pdf={pdf}
                  title={reportVerbiage[languageToUse].upcomingExercises}
                  exercises={exercises[2]}
                  language={language}
                  between={
                    <div
                      className={[
                        'rounded-md bg-yellow-100 my-6 avoid-break',
                        pdf ? 'pb-4 px-4' : 'p-4',
                      ].join(' ')}
                    >
                      <div className="flex">
                        <div className={['shrink-0', pdf ? 'mt-2' : ''].join(' ')}>
                          <ExclamationTriangleIcon
                            className={['h-6 w-6 text-yellow-600'].join(' ')}
                            aria-hidden="true"
                          />
                        </div>
                        <div className="ml-3">
                          <h3 className="text-base font-semibold text-yellow-900">
                            {reportVerbiage[languageToUse].stop}
                          </h3>
                          <div className="mt-2 text-md font-semibold text-yellow-800">
                            <p>{reportVerbiage[languageToUse].followingUpcomingExercises}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  }
                />
              </div>
            )}
            {clinicId === 5 && !pdf && (
              <div className="inline-block">
                <div className="flex flex-col items-center mt-12 avoid-break">
                  <a
                    href="https://www.premierrehab.com/pages/shop"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <img src={`${S3_URL}/Shop+Now.png`} alt="Shop Now"></img>
                  </a>
                  <span className="font-semibold mt-2">
                    or visit{' '}
                    <a
                      href="https://www.premierrehab.com/pages/shop"
                      target="_blank"
                      rel="noreferrer"
                      className="no-underline font-medium"
                    >
                      https://www.premierrehab.com/pages/shop
                    </a>
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportPrint;
